import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Briefcase,
} from "lucide-react";
import { isInDevelopment, getServiceStyles } from "../config/serviceStatus";
import { useLanguage } from "../contexts/LanguageContext";
import usePageTitle from '../hooks/usePageTitle';
import {
  getMemberImage,
  getMemberImagePosition,
  getMemberImageScale,
} from "../data/teamMemberImages";

const DURATION_MS = 2500;
const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
/** Team keys in display order (matches Teams page). */
const PEOPLE_TEAM_ORDER = [
  "aiSoftwareEngineerTeam",
  "securityTeam",
  "productTeam",
  "dataTeam",
  "marketingTeam",
  "legalTeam",
  "uiuxTeam",
  "hrTeam",
  "mentorsTeam",
];

const Home = () => {
  const { t } = useLanguage();
  usePageTitle(null);
  const location = useLocation();
  const navigate = useNavigate();
  const servicesScrollRef = useRef(null);
  const impactSectionRef = useRef(null);
  const didInitialScrollToTop = useRef(false);
  const skipScrollToHashOnce = useRef(false);
  const hasCountedUp = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [impactValues, setImpactValues] = useState([0, 0, 0, 0]);
  

  // Flatten all team members for the people strip, deduplicated by name so everyone appears once (equal presence in loop)
  const peopleList = useMemo(() => {
    const raw = t("home.teamMembers") || {};
    const flat = [];
    for (const key of PEOPLE_TEAM_ORDER) {
      const members = raw[key];
      if (Array.isArray(members)) flat.push(...members);
    }
    if (flat.length > 0) {
      const seen = new Set();
      return flat.filter((m) => {
        if (seen.has(m.name)) return false;
        seen.add(m.name);
        return true;
      });
    }
    return ["boardMember0", "boardMember1", "boardMember2", "boardMember3", "boardMember4", "boardMember5"]
      .map((k) => {
        const b = t(`home.teams.${k}`);
        return b && typeof b === "object" && b.name
          ? { name: b.name, role: b.role }
          : null;
      })
      .filter(Boolean);
  }, [t]);

  // After refresh or first load with a hash (e.g. #about), start at top and clear hash so user isn’t stuck at a section
  useEffect(() => {
    if (didInitialScrollToTop.current) return;
    didInitialScrollToTop.current = true;
    if (location.hash) skipScrollToHashOnce.current = true;
    window.scrollTo(0, 0);
    if (location.hash) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.hash, navigate]);

  const updateScrollArrows = useCallback(() => {
    const el = servicesScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = servicesScrollRef.current;
    if (!el) return;
    const run = () => {
      updateScrollArrows();
    };
    run();
    const raf = requestAnimationFrame(run);
    const ro = new ResizeObserver(run);
    ro.observe(el);
    el.addEventListener("scroll", run);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("scroll", run);
    };
  }, [updateScrollArrows]);

  // Scroll to section when hash changes (e.g. user clicked About), but not on initial load with hash (we scroll to top instead)
  useEffect(() => {
    const hash = location.hash;
    if (!hash) return;
    if (skipScrollToHashOnce.current) {
      skipScrollToHashOnce.current = false;
      return;
    }
    const el = document.querySelector(hash);
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  // Count-up animation only when Impact section scrolls into view (not if already visible on load)
  useEffect(() => {
    const section = impactSectionRef.current;
    if (!section || hasCountedUp.current) return;
    const targets = [2500, 5, 40, 200]; // placeholder targets – replace with real data later
    const threshold = 0.2;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || hasCountedUp.current) return;
        const ratio = entry.intersectionRatio;
        const inView = ratio >= threshold;
        if (!inView) return;
        hasCountedUp.current = true;
        const staggerDelay = 300;
        targets.forEach((target, idx) => {
          const delay = idx * staggerDelay;
          const startTime = performance.now() + delay;
          const tick = (now) => {
            const elapsed = now - startTime;
            if (elapsed < 0) { requestAnimationFrame(tick); return; }
            const progress = Math.min(elapsed / DURATION_MS, 1);
            const eased = easeOutExpo(progress);
            setImpactValues((prev) => {
              const next = [...prev];
              next[idx] = Math.round(target * eased);
              return next;
            });
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3], rootMargin: "0px" },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  

  const scrollServices = (direction) => {
    const el = servicesScrollRef.current;
    if (!el) return;
    const card = el.querySelector(".service-card");
    const gapPx = 16; // gap-4
    const step = card ? card.offsetWidth + gapPx : el.clientWidth / 3.25;
    el.scrollBy({
      left: direction === "left" ? -step : step,
      behavior: "smooth",
    });
  };

  const features = [
    {
      title: t("home.features.sillyDragon.title"),
      description: t("home.features.sillyDragon.description"),
      link: "/silly-dragon",
      image: "/images/silly-dragon-project-card.png",
      imagePosition: "center 35%",
      glowColor: "group-hover:shadow-glow-orange",
      badgeKey: "home.badgeEarlyAccess",
    },
    {
      title: t("home.features.agentBi.title"),
      description: t("home.features.agentBi.description"),
      link: "/agent-bi",
      image: "/images/agent-bi-card.png",
      glowColor: "group-hover:shadow-glow-blue",
    },
    {
      title: t("home.features.education.title"),
      description: t("home.features.education.description"),
      link: "/education",
      image: "/images/education-project-card.png",
      glowColor: "group-hover:shadow-glow-orange",
      badgeKey: "home.badgeMvp",
    },
    {
      title: t("home.features.forum.title"),
      description: t("home.features.forum.description"),
      link: "/info",
      image: "/images/information-project-card.png",
      glowColor: "group-hover:shadow-glow-green",
      imageFit: "contain",
    },
    {
      title: t("home.features.health.title"),
      description: t("home.features.health.description"),
      link: "#",
      image: "/images/health-project-card.png",
      glowColor: "group-hover:shadow-glow-green",
      badgeKey: "home.badgePlanning",
    },
    {
      title: t("home.features.community.title"),
      description: t("home.features.community.description"),
      link: "/aid",
      image: "/images/aid-project-card.png",
      glowColor: "group-hover:shadow-glow-red",
      imageFit: "contain",
    },
    {
      title: t("home.moreProjectsToCome"),
      description: t("home.moreProjectsToComeDesc"),
      link: null,
      image: null,
      placeholder: true,
    },
  ];

  const stats = [
    { target: 2500, prefix: "$", label: t("home.stats.financialSupport") },
    { target: 5, prefix: "", label: t("home.stats.earlyTalent") },
    { target: 40, prefix: "", label: t("home.stats.volunteers") },
    { target: 200, prefix: "", label: t("home.stats.communityMembers") },
  ];

  return (
    <div className="-mt-16 overflow-x-hidden">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh" />
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-40" />

        {/* Decorative orbs: hidden on mobile to reduce GPU cost from blur + animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
          <div className="absolute -top-32 -right-32 w-[700px] h-[700px] bg-gradient-to-br from-[#4796E3]/15 via-[#4796E3]/08 to-transparent rounded-full blur-[100px] animate-pulse-glow" />
          <div
            className="absolute -bottom-48 -left-48 w-[600px] h-[600px] bg-gradient-to-tr from-[#008744]/10 via-[#008744]/08 to-transparent rounded-full blur-[100px] animate-pulse-glow"
            style={{ animationDelay: "2s" }}
          />
          <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-[#4796E3]/06 to-[#ffa700]/06 rounded-full blur-[80px] animate-float" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-6 sm:pt-36 sm:pb-8 md:pt-44 md:pb-10 w-full">
          <div className="max-w-3xl relative z-10">
            <h1 className="text-3xl sm:text-5xl sm:whitespace-nowrap md:text-6xl lg:text-[4.5rem] xl:text-[5rem] font-bold tracking-tight leading-[1.05] mb-5 overflow-visible relative z-[30] flex flex-wrap items-baseline">
              <span className="text-gradient-animated-yellow-hero inline-block align-baseline">
                {t("home.heroTitle")}
              </span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 font-semibold leading-relaxed mb-8 max-w-2xl">
              {t("home.heroTagline")}
              <br />
              <span className="text-sm sm:text-base font-normal text-gray-400 block mt-1">
                {t("home.heroTaglineSmall")}
              </span>
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-dark to-transparent" />
      </section>

      {/* ===== SERVICES ===== */}
      <section
        id="projects"
        className="relative pt-4 pb-10 md:pt-6 md:pb-12 overflow-hidden"
      >
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-20" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4 md:mb-5">
            <h2 className="flex items-center gap-2 text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-accent" />
              {t("home.projectsTitle")}
            </h2>
          </div>

          <div className="relative flex items-center gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => scrollServices("left")}
              disabled={!canScrollLeft}
              aria-label={t("common.previous")}
              className="flex-shrink-0 z-10 min-w-[44px] min-h-[44px] w-11 h-11 rounded-full border border-dark-border bg-dark-card flex items-center justify-center text-gray-400 hover:text-white active:scale-95 sm:bg-dark-card/90 sm:backdrop-blur-sm hover:border-dark-border-light disabled:opacity-40 disabled:pointer-events-none transition-all duration-200 shadow-dark-card touch-manipulation"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div
              ref={servicesScrollRef}
              className="flex overflow-x-auto gap-4 scroll-smooth scrollbar-hide snap-x snap-mandatory py-2 -mx-1 touch-pan-x overscroll-x-contain"
            >
              {features.map((feature, index) => {
                const isPlaceholder =
                  feature.placeholder || feature.link == null;
                const isDev = !isPlaceholder && isInDevelopment(feature.link);
                const styles = isPlaceholder
                  ? { container: "", cursor: "cursor-default" }
                  : getServiceStyles(feature.link);
                const cardClass = `service-card group relative flex-shrink-0 w-[min(calc((100%-3rem)/3.25),100%)] min-w-[260px] sm:min-w-[280px] h-[340px] sm:h-[380px] flex flex-col rounded-xl overflow-hidden transition-all duration-400 snap-start ${
                  isPlaceholder || isDev
                    ? `border border-dark-border bg-dark-card/80 ${styles.cursor} ${isPlaceholder ? "border-dashed opacity-80" : styles.container}`
                    : `border border-dark-border bg-dark-card hover:-translate-y-1 hover:border-accent/40 ${feature.glowColor || ""}`
                }`;
                const content = (
                  <>
                    <span className="absolute top-2 right-2 z-10 text-[11px] px-2.5 py-1.5 font-semibold uppercase tracking-wide text-accent bg-dark-surface border border-accent/30 rounded-md">
                      {feature.badgeKey ? t(feature.badgeKey) : t("home.badge1")}
                    </span>
                    <div className="flex-1 min-h-0 w-full bg-dark-surface flex items-center justify-center overflow-hidden relative">
                      {feature.image ? (
                        <>
                          <img
                            src={feature.image}
                            alt=""
                            loading={index < 2 ? undefined : 'lazy'}
                            className={`w-full h-full ${feature.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
                            style={feature.imagePosition ? { objectPosition: feature.imagePosition } : undefined}
                          />
                          {feature.link === "/agent-bi" && (
                            <div className="absolute left-0 right-0 pointer-events-none flex justify-center" style={{ top: '40%' }}>
                              <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                                {['boardMember0', 'boardMember1', 'boardMember2', 'boardMember3', 'boardMember4', 'boardMember5'].map((key) => {
                                  const name = t(`home.teams.${key}.name`);
                                  const img = getMemberImage(name);
                                  if (!img) return null;
                                  return (
                                    <div
                                      key={key}
                                      className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-white/40 overflow-hidden bg-dark-surface shadow-lg"
                                    >
                                      <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: getMemberImagePosition(name), ...(getMemberImageScale(name) && { transform: getMemberImageScale(name) }) }} />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="opacity-40 text-xs">—</span>
                      )}
                    </div>
                    <div className="flex-shrink-0 p-3 sm:p-4 flex flex-col">
                      <h3
                        className={`text-base sm:text-lg font-semibold mb-1 ${isPlaceholder || isDev ? "text-gray-500" : "text-white"}`}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className={`text-sm leading-relaxed line-clamp-2 ${isPlaceholder || isDev ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {feature.description}
                      </p>
                      {!isDev && !isPlaceholder && (
                        <span className="inline-flex items-center mt-1.5 text-sm font-medium text-accent opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
                          {t("common.learnMore")}{" "}
                          <ChevronRight className="ml-0.5 h-4 w-4" />
                        </span>
                      )}
                    </div>
                  </>
                );
                if (isPlaceholder) {
                  return (
                    <div
                      key={index}
                      className={cardClass}
                      title={t("home.moreProjectsToComeDesc")}
                    >
                      {content}
                    </div>
                  );
                }
                return (
                  <Link
                    key={index}
                    to={feature.link}
                    className={cardClass}
                    title={isDev ? t("common.inDevelopmentDesc") : ""}
                  >
                    {content}
                  </Link>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => scrollServices("right")}
              disabled={!canScrollRight}
              aria-label={t("common.next")}
              className="flex-shrink-0 z-10 min-w-[44px] min-h-[44px] w-11 h-11 rounded-full border border-dark-border bg-dark-card flex items-center justify-center text-gray-400 hover:text-white active:scale-95 sm:bg-dark-card/90 sm:backdrop-blur-sm hover:border-dark-border-light disabled:opacity-40 disabled:pointer-events-none transition-all duration-200 shadow-dark-card touch-manipulation"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="relative z-20 py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="flex items-center gap-2 text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight mb-4 md:mb-5">
            <Sparkles className="h-4 w-4 flex-shrink-0 text-accent" />
            {t("home.aboutTitle")}
          </h2>
          <div className="shimmer-wrap block border-gradient bg-dark-card rounded-2xl p-8 md:p-12 hover:shadow-glow-blue transition-all duration-400">
            <p className="text-gray-300 text-sm sm:text-base md:text-lg max-w-2xl mx-auto text-center leading-relaxed m-0 mb-4 font-semibold">
              {t("home.heroDescription")}
            </p>
            <p className="text-gray-300 text-sm sm:text-base md:text-lg max-w-2xl mx-auto text-center leading-relaxed m-0 font-semibold">
              {t("home.heroDescription2")}
            </p>
          </div>
        </div>
      </section>

      {/* ===== IMPACT ===== */}
      <section
        id="impact"
        ref={impactSectionRef}
        className="relative py-8 sm:py-10 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="flex items-center gap-2 text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight mb-5 md:mb-6">
            <Sparkles className="h-4 w-4 flex-shrink-0 text-accent" />
            {t("home.impactTitle")}
          </h2>
          <div className="max-w-5xl mx-auto">
            <div className="border-gradient bg-dark-card rounded-2xl px-4 py-5 sm:px-8 md:px-10 sm:py-6 md:py-8 shadow-dark-card">
              <div className="grid grid-cols-1 divide-y divide-dark-border sm:grid-cols-4 sm:divide-y-0 sm:divide-x">
                {stats.map((stat, i) => (
                  <div
                    key={i}
                    className="text-center px-3 py-4 first:pt-0 last:pb-0 sm:py-0 sm:px-4"
                  >
                    <div
                      className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight ${i === 0 ? "text-google-yellow" : i === 1 ? "text-google-blue" : i === 2 ? "text-google-green" : "text-google-red"}`}
                    >
                      {stat.prefix}
                      {impactValues[i].toLocaleString()}{" "}
                      <span className="text-base sm:text-lg md:text-xl font-medium opacity-90">
                        +
                      </span>
                    </div>
                    <div className="text-sm sm:text-base md:text-lg text-white font-bold mt-1.5">
                      {stat.label}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {t("home.stats.andCounting")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PEOPLE (Teams: moving cards + View all) ===== */}
      <section
        id="teams"
        className="relative py-8 sm:py-10 md:py-12 overflow-hidden"
      >
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-20 pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5 md:mb-6">
            <h2 className="flex items-center gap-2 text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-accent" />
              {t("home.peopleTitle")}
            </h2>
            <Link
              to="/teams"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent-hover transition-colors"
            >
              {t("home.teamsViewAll")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="space-y-4">
            <div className="teams-cards-outer -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="teams-cards-track">
                {[1, 2, 3, 4].map((copy) => (
                  <div key={copy} className="flex gap-4 flex-shrink-0 pr-4">
                    {peopleList.map((person, i) => {
                      const img = getMemberImage(person.name);
                      return (
                        <div
                          key={`${copy}-${i}`}
                          className="bg-dark-card border border-dark-border rounded-xl p-4 min-w-[200px] sm:min-w-[220px] flex items-center gap-3 flex-shrink-0"
                        >
                          <div className="w-11 h-11 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0 overflow-hidden">
                            {img ? (
                              <img
                                src={img}
                                alt={person.name}
                                loading="lazy"
                                className="w-full h-full object-cover"
                                style={{
                                  objectPosition: getMemberImagePosition(person.name),
                                  ...(getMemberImageScale(person.name) && { transform: getMemberImageScale(person.name) }),
                                }}
                              />
                            ) : (
                              "?"
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-medium text-sm truncate">
                              {person.name}
                            </div>
                            <div className="text-accent text-xs truncate">
                              {person.role}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className="teams-cards-outer -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="teams-cards-track teams-cards-track-ltr">
                {[1, 2, 3, 4].map((copy) => (
                  <div key={copy} className="flex gap-4 flex-shrink-0 pr-4">
                    {peopleList.map((person, i) => {
                      const img = getMemberImage(person.name);
                      return (
                        <div
                          key={`${copy}-${i}`}
                          className="bg-dark-card border border-dark-border rounded-xl p-4 min-w-[200px] sm:min-w-[220px] flex items-center gap-3 flex-shrink-0"
                        >
                          <div className="w-11 h-11 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0 overflow-hidden">
                            {img ? (
                              <img
                                src={img}
                                alt={person.name}
                                loading="lazy"
                                className="w-full h-full object-cover"
                                style={{
                                  objectPosition: getMemberImagePosition(person.name),
                                  ...(getMemberImageScale(person.name) && { transform: getMemberImageScale(person.name) }),
                                }}
                              />
                            ) : (
                              "?"
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-medium text-sm truncate">
                              {person.name}
                            </div>
                            <div className="text-accent text-xs truncate">
                              {person.role}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-sm sm:text-base text-gray-400 mt-6 md:mt-8 leading-relaxed max-w-3xl mx-auto">
            {t("home.teamsCredibility")}
          </p>
        </div>
      </section>

      {/* ===== JOIN US ===== */}
      <section
        id="join-us"
        className="relative py-8 sm:py-10 md:py-12 overflow-hidden"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 md:mb-8">
            <h2 className="flex items-center gap-2 text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight mb-3 md:mb-4">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-accent" />
              {t("home.joinUsTitle")}
            </h2>
            {t("home.joinUsSubtitle") && (
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                {t("home.joinUsSubtitle")}
              </p>
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            {/* Roles / Open positions - before the button */}
            <div className="mb-8 md:mb-10">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">
                <Briefcase className="h-4 w-4 text-accent" />
                {t("join.positionsTitle")}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(t("join.positions") || []).map((position, index) => (
                  <Link
                    key={index}
                    to={`/careers?position=${encodeURIComponent(position.title)}`}
                    className="group block w-full text-left border border-dark-border bg-dark-card rounded-xl px-5 py-4 hover:border-accent/40 hover:shadow-[0_0_24px_-4px_rgba(71,150,227,0.2)] hover:-translate-y-0.5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-dark"
                  >
                    <div className="font-semibold text-white text-base tracking-tight group-hover:text-accent transition-colors duration-200">
                      {position.title}
                    </div>
                    <p className="text-gray-400 text-sm mt-1.5 leading-relaxed line-clamp-2">
                      {position.desc}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                      {position.type}
                    </span>
                    <span className="mt-2 flex items-center gap-1 text-xs font-medium text-accent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {t("footer.about.join")}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <p
              className="text-center text-sm sm:text-base mb-6 font-bold text-google-yellow"
              style={{
                textShadow:
                  "0 0 20px rgba(251, 188, 4, 0.5), 0 0 40px rgba(251, 188, 4, 0.25)",
              }}
            >
              {t("join.proposeRoleHint")}
            </p>
            <div className="text-center">
              <Link
                to="/careers"
                className="shimmer-wrap group relative inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-dark-card font-semibold text-sm rounded-full hover:shadow-glow-white transition-all duration-400"
              >
                {t("footer.about.join")}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      
    </div>
  );
};

export default Home;
